import {Escort} from '../types';
import ApiClient from '../services/ApiClient';
import {logDebug} from '../utils/log';
import {CACHE_CONFIG, CacheData} from '../config/cache';
import {API_CONFIG} from '../config/api';
import {parseBlockedLocation, parseLangsString, parseServicesString} from '../utils/serviceStringParser';
import {parseOrariString} from '../utils/orariStringParser';
import {parseTariffeString} from '../utils/tariffeStringParser';

// Interfaccia per i dati API
interface ApiEscortData {
  id?: string;
  _id?: string;
  name?: string;
  age?: string | number;
  category: string;
  location?: string;
  blocked?: string;
  orari?: string;
  tariffeIn?: string;
  tariffeOut?: string;
  langs?: string;
  description?: string;
  services?: string;
  imageUrl?: string;
  image?: string;
   rating?: string | number;
   price?: string;
   phone?: string;
   pphone?: string;
   email?: string;
   featured?: string;
  isVetrina?: string;
  isActive?: string;
  inHoliday?: string;
  vetrina?: boolean;
  isVerified?: string;
  verified?: boolean;
  whatsappNumber?: string;
  whatsapp?: string;
  nationality?: string;
  physicalFeatures?: Record<string, string>;
  views?: number;
}

interface SearchCriteria {
  location?: string;
  featured?: boolean;
  isVetrina?: boolean;
  age?: number;
  id?: string;
  [key: string]: string | number | boolean | undefined;
}

export type { SearchCriteria };

const API_URL = import.meta.env.VITE_API_URL || 'https://api.escortfun.net/api/v2/index.php';
const API_TOKEN = import.meta.env.VITE_API_TOKEN || 'readesc';

const apiClient = new ApiClient(API_URL, API_TOKEN);

const getCachedData = (countryModel?: string): Escort[] | null => {
  try {
    const cacheKey = countryModel ? `${CACHE_CONFIG.ESCORTS.KEY}_${countryModel}` : CACHE_CONFIG.ESCORTS.KEY;
    const cachedItem = localStorage.getItem(cacheKey);
    if (!cachedItem) return null;
    
    const cache: CacheData<Escort[]> = JSON.parse(cachedItem);
    const now = Date.now();
    
    // Verifica se la cache è scaduta
    if (now - cache.timestamp > CACHE_CONFIG.ESCORTS.DURATION) {
      localStorage.removeItem(cacheKey);
      return null;
    }
    
    logDebug('📦 Dati caricati dalla cache locale (età: ' + Math.round((now - cache.timestamp) / 1000 / 60) + ' minuti)');
    return cache.data;
  } catch (error) {
    logDebug('⚠️ Errore durante il caricamento della cache: ' + error);
    const cacheKey = countryModel ? `${CACHE_CONFIG.ESCORTS.KEY}_${countryModel}` : CACHE_CONFIG.ESCORTS.KEY;
    localStorage.removeItem(cacheKey);
    return null;
  }
};

const setCachedData = (data: Escort[], countryModel?: string): void => {
  try {
    const cache: CacheData<Escort[]> = {
      data,
      timestamp: Date.now()
    };
    const cacheKey = countryModel ? `${CACHE_CONFIG.ESCORTS.KEY}_${countryModel}` : CACHE_CONFIG.ESCORTS.KEY;
    localStorage.setItem(cacheKey, JSON.stringify(cache));
    logDebug('💾 Dati salvati nella cache locale (' + data.length + ' escort per ' + (countryModel || 'default') + ')');
  } catch (error) {
    logDebug('⚠️ Errore durante il salvataggio della cache: ' + error);
  }
};

// Funzione per pulire tutte le cache dei paesi
export const clearCountriesCache = (): void => {
  try {
    // Pulisce cache default
    localStorage.removeItem(CACHE_CONFIG.ESCORTS.KEY);
    
    // Pulisce cache per ogni paese configurato
    Object.values(API_CONFIG.COUNTRIES).forEach(country => {
      const cacheKey = `${CACHE_CONFIG.ESCORTS.KEY}_${country.model}`;
      localStorage.removeItem(cacheKey);
    });
    
    logDebug('🧹 Cache di tutti i paesi pulita');
  } catch (error) {
    logDebug('⚠️ Errore durante la pulizia della cache: ' + error);
  }
};

// Funzione per ottenere informazioni sulla cache (utile per debug)
export const getCacheInfo = () => {
  const info: { [key: string]: any } = {};
  
  // Cache default
  const defaultCache = localStorage.getItem(CACHE_CONFIG.ESCORTS.KEY);
  if (defaultCache) {
    try {
      const cache = JSON.parse(defaultCache);
      info['default'] = {
        count: cache.data?.length || 0,
        age: Math.round((Date.now() - cache.timestamp) / 1000 / 60),
        expired: (Date.now() - cache.timestamp) > CACHE_CONFIG.ESCORTS.DURATION
      };
    } catch (e) {
      info['default'] = { error: 'Invalid cache data' };
    }
  }
  
  // Cache per ogni paese
  Object.values(API_CONFIG.COUNTRIES).forEach(country => {
    const cacheKey = `${CACHE_CONFIG.ESCORTS.KEY}_${country.model}`;
    const countryCache = localStorage.getItem(cacheKey);
    if (countryCache) {
      try {
        const cache = JSON.parse(countryCache);
        info[country.model] = {
          count: cache.data?.length || 0,
          age: Math.round((Date.now() - cache.timestamp) / 1000 / 60),
          expired: (Date.now() - cache.timestamp) > CACHE_CONFIG.ESCORTS.DURATION
        };
      } catch (e) {
        info[country.model] = { error: 'Invalid cache data' };
      }
    }
  });
  
  return info;
};

const enhanceEscortData = (escort: Escort): Escort => {
  const enhancedEscort = { ...escort };

  if (!escort.description || escort.description.length < 100) {
    const descriptions = [
      `Ciao, sono ${escort.name}, una ${escort.nationality || 'affascinante'} escort di ${escort.age} anni che vive a ${escort.location.split(' / ')[1] || escort.location}. Sono una donna raffinata ed elegante, sempre attenta ai dettagli e con una particolare predisposizione per l'arte della conversazione. Mi piace accompagnare gentiluomini distinti a cene di lavoro, eventi sociali e momenti privati di relax. La mia personalità solare e la mia discrezione sono caratteristiche che i miei clienti apprezzano particolarmente.`,
      
      `Mi chiamo ${escort.name} e sono una escort professionale di ${escort.age} anni con base a ${escort.location.split(' / ')[1] || escort.location}. Sono ${escort.nationality || 'una donna'} con un carattere dolce ma determinato, sempre elegante e curata nei dettagli. Amo l'arte, la cultura e i viaggi, e sono sempre disponibile per accompagnare i miei clienti in eventi mondani, cene romantiche o semplici momenti di intimità e relax.`,
      
      `Sono ${escort.name}, ${escort.age} anni, ${escort.nationality || ''} e vivo a ${escort.location.split(' / ')[1] || escort.location}. Sono una donna sofisticata con una passione per le cose belle della vita. Mi caratterizzo per la mia eleganza naturale, la mia cultura e la mia capacità di adattarmi a qualsiasi situazione sociale. Che si tratti di una cena di gala, un weekend fuori porta o un momento intimo, saprò essere la compagnia perfetta.`
    ];
    
    enhancedEscort.description = descriptions[Math.floor(Math.random() * descriptions.length)];
  }
  
  if (!escort.services || escort.services.length === 0) {
    enhancedEscort.services = [
      { name: 'Massaggio rilassante' },
    ];
  }
  
  if (!escort.physicalFeatures || Object.keys(escort.physicalFeatures).length === 0) {
    const hairColors = ['Biondo', 'Castano', 'Nero', 'Rosso', 'Biondo cenere'];
    const eyeColors = ['Blu', 'Verdi', 'Marroni', 'Nocciola', 'Grigi'];
    const heights = ['160 cm', '165 cm', '168 cm', '170 cm', '172 cm', '175 cm'];
    const weights = ['50 kg', '52 kg', '55 kg', '58 kg', '60 kg'];
    
    enhancedEscort.physicalFeatures = {
      hair: hairColors[Math.floor(Math.random() * hairColors.length)],
      eyes: eyeColors[Math.floor(Math.random() * eyeColors.length)],
      height: heights[Math.floor(Math.random() * heights.length)],
      weight: weights[Math.floor(Math.random() * weights.length)]
    };
  }
  
  if (!escort.whatsappNumber && (escort.phone || escort.pphone)) {
    enhancedEscort.whatsappNumber = escort.pphone || escort.phone;
  }
  
  // Do NOT copy `phone` into `pphone` — `pphone` is provided by the API
  // and must remain distinct. Keep `pphone` undefined if the API didn't supply it.
  
  if (!escort.nationality) {
    const nationalities = ['Italiana', 'Brasiliana', 'Rumena', 'Russa', 'Spagnola', 'Francese'];
    enhancedEscort.nationality = nationalities[Math.floor(Math.random() * nationalities.length)];
  }
  
  return enhancedEscort;
};

const mapApiDataToEscort = (apiData: ApiEscortData): Escort => {
  return {
    id: apiData.id || apiData._id || '',
    name: apiData.name || '',
    category: apiData.category || '',
    age: parseInt(String(apiData.age)) || 18,
    location: apiData.location || '',
    blocked: parseBlockedLocation(apiData.blocked || ''),
    orari: parseOrariString(apiData.orari || ''),
    tariffeIn: parseTariffeString(apiData.tariffeIn || ''),
    tariffeOut: parseTariffeString(apiData.tariffeOut || ''),
    langs: parseLangsString(apiData.langs),
    description: apiData.description || '',
    services: parseServicesString(apiData.services),
    imageUrl: apiData.imageUrl || apiData.image || '',
    rating: parseFloat(String(apiData.rating)) || 0,
    price: apiData.price || '',
    phone: apiData.phone || '',
    pphone: apiData.pphone || '',
    email: apiData.email || '',
    featured: Boolean(apiData.featured !== "false"),
    isVetrina: Boolean(apiData.isVetrina !== "false"),
    isVerified: Boolean(apiData.isVerified !== "false"),
    isActive: Boolean(apiData.isActive !== "false"),
    inHoliday: Boolean(apiData.inHoliday !== "false"),
    whatsappNumber: apiData.whatsappNumber || apiData.whatsapp || '',
    nationality: apiData.nationality || '',
    physicalFeatures: apiData.physicalFeatures || {},
    views: apiData.views || 0,
  };
};

// Funzione per fetchare tutti gli escort
export const fetchEscorts = async (countryModel?: string): Promise<Escort[]> => {
  const modelToUse = countryModel || API_CONFIG.MODELS.ESCORTS;
  
  try {
    
    // Controlla prima se ci sono dati in cache validi
    const cachedData = getCachedData(modelToUse);
    if (cachedData) {
      return cachedData;
    }
    
    logDebug('🚀 Inizio fetchEscorts...');
    logDebug('📍 API URL:' + API_URL);
    logDebug('🔑 API Token:' + API_TOKEN ? API_TOKEN.substring(0, 10) + '...' : 'NON CONFIGURATO');
    
    const response = await apiClient.getModel(modelToUse);
    logDebug('📡 Risposta API ricevuta:' + response);
    
    if (response && response.instances && Array.isArray(response.instances)) {
      logDebug('✅ Dati validi ricevuti:' +  response.instances.length + 'elementi');
      const mappedData = response.instances
          .filter((instance: ApiEscortData) => instance.isActive !== "false")
          .map(mapApiDataToEscort);
      logDebug('🔄 Dati mappati:' + mappedData.length + 'escort');
      logDebug('👥 Primo escort mappato:' + mappedData[0]?.name || 'nessuno');
      
      // Arricchisci i dati se necessario
      const enhancedData = mappedData.map(enhanceEscortData);
      
      // Salva i dati nella cache
      setCachedData(enhancedData, modelToUse);
      
      return enhancedData;
    } else {
      logDebug('⚠️ Formato risposta API non valido:' + typeof response + response);
      throw new Error('API non ha restituito dati validi');
    }
  } catch (error) {
    logDebug('❌ Errore durante fetchEscorts:' + error);
      // Se l'API fallisce, prova a restituire dati dalla cache anche se scaduti
    try {
      const cacheKey = modelToUse ? `${CACHE_CONFIG.ESCORTS.KEY}_${modelToUse}` : CACHE_CONFIG.ESCORTS.KEY;
      const staleCache = localStorage.getItem(cacheKey);
      if (staleCache) {
        const cache: CacheData<Escort[]> = JSON.parse(staleCache);
        logDebug('📦 Restituendo dati dalla cache scaduta come fallback per ' + modelToUse);
        return cache.data;
      }
    } catch (cacheError) {
      logDebug('⚠️ Anche il fallback della cache è fallito: ' + cacheError);
    }
    
    throw error; // Rilanciamo l'errore se non ci sono alternative
  }
};

export const searchEscorts = async (criteria: SearchCriteria, countryModel?: string): Promise<Escort[]> => {
  try {
    const modelToUse = countryModel || API_CONFIG.MODELS.ESCORTS;
    const response = await apiClient.searchPublic(modelToUse, criteria);
    if (response && response.instances && Array.isArray(response.instances)) {
      const mappedData = response.instances
          .filter((instance: ApiEscortData) => instance.isActive !== "false")
          .map(mapApiDataToEscort);
      return mappedData.map(enhanceEscortData);
    }
    throw new Error('API search non ha restituito dati validi');
  } catch (error) {
    logDebug('Error searching escorts:' + error);
    throw error; // Rilanciamo l'errore invece di usare fallback
  }
};

// Funzione per ottenere un escort specifico
export const getEscortById = async (id: string, countryModel?: string): Promise<Escort | null> => {
  try {
    logDebug('🔍 getEscortById - Cercando escort con ID:' + id);
    const params = { id: {} };
    if (id) {
      params.id = { "equal": id };
    }
    const modelToUse = countryModel || API_CONFIG.MODELS.ESCORTS;
    const response = await apiClient.search(modelToUse, params);
    logDebug('📡 Risposta API per getEscortById:' + response);
    
    if (response && response.results && Array.isArray(response.results) && response.results.length > 0) {
      logDebug('✅ Escort trovato dall\'API:' + response.results[0].name || 'nome non disponibile');
      const mappedEscort = mapApiDataToEscort(response.results[0]);
      return enhanceEscortData(mappedEscort);
    } else {
      logDebug('❌ API non ha restituito risultati per ID:' + id);
      throw new Error(`Escort con ID ${id} non trovato`);
    }
  } catch (error) {
    logDebug('❌ Errore in getEscortById:' + error);
    throw error; // Rilanciamo l'errore invece di usare fallback
  }
};

export const updateEscortViews = async (id: string, views: number, countryModel?: string): Promise<void> => {
  try {
    const modelToUse = countryModel || API_CONFIG.MODELS.ESCORTS;
    await apiClient.updateData(modelToUse, { id }, { views });
  } catch (error) {
    logDebug('Error updating escort views:', error);
  }
}
